import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

const ToastContext = createContext(null);

const VARIANTS = {
  success: { icon: '✓', ring: 'ring-emerald-200', bar: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-800' },
  error: { icon: '!', ring: 'ring-red-200', bar: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-800' },
  info: { icon: 'i', ring: 'ring-indigo-200', bar: 'bg-indigo-500', bg: 'bg-indigo-50', text: 'text-indigo-800' },
};

let idSeq = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4200) => {
    const id = ++idSeq;
    setToasts((prev) => [...prev, { id, message, type }]);
    timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none">
        {toasts.map((t) => {
          const v = VARIANTS[t.type] || VARIANTS.info;
          return (
            <div
              key={t.id}
              className={`toast-in pointer-events-auto flex items-start gap-3 rounded-2xl shadow-xl ring-1 ${v.ring} ${v.bg} px-4 py-3 backdrop-blur-xl`}
            >
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${v.bar} text-white text-xs font-bold`}>
                {v.icon}
              </span>
              <p className={`text-sm font-medium leading-snug ${v.text}`}>{t.message}</p>
              <button
                onClick={() => dismiss(t.id)}
                className="ml-auto shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
